import { useViewport } from 'components/general/useViewPort';
import React, { useMemo } from "react";
import Competitor from 'components/presentation/Competitor';
import { NocMemo as Noc } from 'components/presentation/Noc';
import { ColumnExpander, ColumnNameH2H, ColumnBib } from "components/common/TableColumns"
import StatsBox from "components/sports/bk3/views/StatsBox"
import { dateFormat } from "helpers/utils"
import Label from "components/general/Label"
import { capitalizeFirstLetter } from 'helpers/utils'

import LoaderSubComponent from "components/general/LoaderSubComponent";
import MirsTable from "components/general/table/Table";
import { useUrlFriendlyToParameters } from "helpers/urlFriendly";

const TableColumns = () => {
  return (
    [
      ColumnBib({ disableSort: true, label: 'HeaderNumber' }),
      ColumnNameH2H({ showSuspendedCode: true, hideNoc: true }),
      ColumnExpander({ type: 'alwaysTrue', idNode: 'athlete' })
    ]
  )
}

const TableColumnsKrakow = () => {
  return (
    [
      ColumnBib({ disableSort: true, label: 'HeaderNumber' }),
      ColumnNameH2H({ showSuspendedCode: true, hideNoc: true }),
    ]
  )
}

const getConfigurationBox = (date) => {

  const formatedDate = date ? dateFormat(date, "", "D MMM H:mm") : "";

  let configuration = {
    header: `Current Tournament Statistics - ${formatedDate}`,
    stats: [
      {
        order: 1,
        code: 'MP',
        attr: 'value',
        title: 'StatsGamesPlayed',
        label: 'Games played'
      },
      {
        order: 2,
        code: 'MINS',
        attr: 'value',
        title: 'StatsMinutesPlayed',
        label: 'Minutes Played per Game'
      },
      {
        order: 3,
        code: 'PTS',
        attr: 'value',
        title: 'StatsPointsGame',
        label: 'Points per Game'
      },
      {
        order: 4,
        code: 'P_VAL',
        attr: 'value',
        title: 'StatsPlayerValue',
        label: 'Player Value'
      },
      {
        order: 5,
        code: 'S_EFF',
        attr: 'value',
        title: 'StatsShootingEfficiency',
        label: 'Shooting Efficiency'
      },
      {
        order: 6,
        code: 'S_VAL',
        attr: 'value',
        title: 'StatsShootingValue',
        label: 'Shooting Value'
      },
      {
        order: 7,
        code: 'P1',
        attr: 'value',
        attr2: 'attempt',
        title: 'StatsP1',
        label: '1-Point Shots Made/Attempted '
      },
      {
        order: 8,
        code: 'FT',
        attr: 'value',
        title: 'StatsFT',
        label: 'Free Throws Made/Attempted'
      },
      {
        order: 9,
        code: 'P2',
        attr: 'value',
        title: 'StatsP2',
        label: '2-Point Shots Made/Attempted '
      },
      {
        order: 10,
        code: 'HGL',
        attr: 'value',
        title: 'StatsHighlightsPerGame',
        label: 'Highlights per Game'
      },
      {
        order: 11,
        code: 'ASSIST',
        attr: 'value',
        title: 'StatsAssists',
        label: 'Key Assists'
      },
      {
        order: 12,
        code: 'DRV',
        attr: 'value',
        title: 'StatsDrives',
        label: 'Drives'
      },
      {
        order: 13,
        code: 'BLC',
        attr: 'value',
        title: 'StatsBlockedShots',
        label: 'Blocked Shots'
      },
      {
        order: 14,
        code: 'BZR',
        attr: 'value',
        title: 'StatsBuzzerBeaters',
        label: 'Buzzerbeaters'
      },
      {
        order: 15,
        code: 'DNK',
        attr: 'value',
        title: 'StatsDunks',
        label: 'Dunks'
      },
      {
        order: 16,
        code: 'TO',
        attr: 'value',
        title: 'StatsTurnovers',
        label: 'Turnovers'
      },
      {
        order: 17,
        code: 'REB_OR',
        attr: 'value',
        title: 'StatsOffensiveRebounds',
        label: 'Offensive Rebounds'
      },
      {
        order: 18,
        code: 'REB_DR',
        attr: 'value',
        title: 'StatsDefensiveRebounds',
        label: 'Defensive Rebounds'
      },
      {
        order: 19,
        code: 'PLUS_MINUS',
        attr: 'value',
        title: 'StatsPlusMinusGame',
        label: 'Plus-Minus (+/-)'
      },
    ]
  }
  return configuration;
}

const getConfiguration = (data, breakPoint, mirs_competition) => {

  let configuration = {
    columns: [],
    subComponent: null,
    caption: null,
    hiddenColumns: [],
    sortBy: [],
    configs: {
    }
  };

  configuration.caption = (
    <div className="d-flex justify-content-between">
      <div className='d-flex align-items-center'>
        <div className='d-none d-md-block me-1'>
          <Noc className='me-1' {...data?.participant?.organisation} />
        </div>
        <strong>
          <Competitor hidePicture={true} {...data?.participant} />
        </strong>
      </div>
      <div>
        <Uniform data={data} />
      </div>
    </div>
  )

  if (mirs_competition === 'EG2023')
    configuration.columns = TableColumnsKrakow();
  else
    configuration.columns = TableColumns();

  return configuration;
}

const Uniform = ({ data }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters();
  const uniform = capitalizeFirstLetter(data?.eventUnitEntries?.find(i => i?.eue_code === 'UNIFORM')?.eue_value?.toLowerCase());
  return (
    <>
      <div>
        <Label data="HeaderTeamJersey" sport={mirs_discipline} />&nbsp;{uniform}
      </div>
    </>
  )
}

const StartListSide = ({ data, resultStatus, config, date }) => {
  const { mirs_rsc } = useUrlFriendlyToParameters();
  const { breakPoint } = useViewport();
  const { mirs_competition } = useUrlFriendlyToParameters()

  const configEvent = getConfiguration(data, breakPoint, mirs_competition);

  const configBox = getConfigurationBox(date);

  const renderRowSubComponent = React.useCallback(({ row }) => (
    <LoaderSubComponent component={
      <StatsBox data={row?.original?.cumStatsItems} data2={row?.original?.statsItems} config={configBox} breakPoint={breakPoint} />
    }
    />
  ), [resultStatus]);

  //console.log(data.teamAthletes);

  return (<>
    {data.teamAthletes && (
      <div className="start-list-side-main">
        <div className="row">
          <div className="col-12">
            <MirsTable
              className={"table-start-list"}
              id={`mirs-table-start-list-${mirs_rsc}-${data?.participant?.code}`}
              data={data.teamAthletes}
              columns={configEvent.columns}
              renderRowSubComponent={renderRowSubComponent}
              keyRow='athlete-code'
              caption={configEvent.caption}
              initialSortBy={configEvent.sortBy}
            />
          </div>
        </div>
      </div>
    )
    }
  </>)
}

export default StartListSide;
